/**
 * Interpolation that turns the measured thresholds into the dense field the
 * gray scale map paints. Split out from the component so the arithmetic can be
 * tested and so the rasteriser has a plain grid to draw.
 */

/** dB step covered by one hatch tile: 0 dB is tile 0, 1-5 dB is tile 1, 6-10 dB is tile 2, and so on. */
const DECIBELS_PER_TILE = 5;

/**
 * Sensitivity assigned to the strip either side of the meridians. The strip is
 * not measured, and printing it at the top of the scale keeps the crosshair
 * legible instead of running it through dark hatching.
 */
const MERIDIAN_STRIP_DECIBELS = 45;

/** Samples inserted between two measured locations, in each direction. */
const SAMPLES_BETWEEN_LOCATIONS = 2;

/** Interpolated cells the field carries per measured location, along each axis. */
export const INTERPOLATED_CELLS_PER_LOCATION = SAMPLES_BETWEEN_LOCATIONS + 1;

export interface GrayScaleFieldConfig {
  elementsInRow: number[];
  padFrontAmountAtEachRow: number[];
  padBackAmountAtEachRow: number[];
  elementsInRowUpscaled: number[];
  startIndexForElementsInRow: number[];
}

/** Tile level per upscaled cell, `null` where the map is left blank. */
export type GrayScaleField = Array<Array<number | null>>;

export const GRAY_SCALE_TILE_BASE_PATH = "img/new_db_images/{img}.png";

/** One hatch tile per 5 dB band, 10 bands total. */
export const GRAY_SCALE_TILE_COUNT = 10;

function grayScaleTileLevel(decibels: number): number {
  return Math.ceil(decibels / DECIBELS_PER_TILE);
}

export function offLatticeGrayScaleTiles(
  input: Array<string | number>,
  offLatticeIndexes: number[] = [],
): string[] {
  const offLattice = new Set(offLatticeIndexes);
  return input.map((value, index) => {
    if (!offLattice.has(index)) return "";
    const tileLevel = grayScaleTileLevel(Number(value));
    return tileLevel >= 0 && tileLevel < GRAY_SCALE_TILE_COUNT ? String(tileLevel) : "";
  });
}

export function latticeValues<T>(input: T[], offLatticeIndexes: number[] = []): T[] {
  const offLattice = new Set(offLatticeIndexes);
  return input.filter((_value, index) => !offLattice.has(index));
}

function repeated(value: number, count: number): number[] {
  return Array.from({ length: count }, () => value);
}

/**
 * Lays the measured rows out as a rectangle, extending each row outwards by
 * repeating its edge value so every row is the same width.
 */
function padRowsToRectangle(thresholds: number[], config: GrayScaleFieldConfig): number[][] {
  const rows: number[][] = [];
  let readIndex = 0;
  config.elementsInRow.forEach((rowLength, rowIndex) => {
    const measured = thresholds.slice(readIndex, readIndex + rowLength);
    readIndex += rowLength;
    rows.push([
      ...repeated(measured[0], config.padFrontAmountAtEachRow[rowIndex]),
      ...measured,
      ...repeated(measured[measured.length - 1], config.padBackAmountAtEachRow[rowIndex]),
    ]);
  });
  return rows;
}

function interpolate(from: number, to: number, step: number): number {
  const stride = (from - to) / (SAMPLES_BETWEEN_LOCATIONS + 1);
  return Math.ceil(from - step * stride);
}

function fillHorizontally(rows: number[][]): number[][] {
  return rows.map((row) => {
    const filled = [row[0]];
    for (let index = 0; index < row.length - 1; index++) {
      for (let step = 1; step <= SAMPLES_BETWEEN_LOCATIONS; step++) {
        filled.push(interpolate(row[index], row[index + 1], step));
      }
      filled.push(row[index + 1]);
    }
    return filled;
  });
}

function fillVertically(rows: number[][]): number[][] {
  const filled = [rows[0]];
  for (let rowIndex = 0; rowIndex < rows.length - 1; rowIndex++) {
    const current = rows[rowIndex];
    const next = rows[rowIndex + 1];
    for (let step = 1; step <= SAMPLES_BETWEEN_LOCATIONS; step++) {
      filled.push(current.map((value, column) => interpolate(value, next[column], step)));
    }
    filled.push(next);
  }
  return filled;
}

/**
 * Interpolation runs per quadrant so that it never smears sensitivity across
 * either meridian, where the field can change abruptly.
 */
function splitIntoQuadrants(rows: number[][], config: GrayScaleFieldConfig): number[][][] {
  const columnSplit = config.elementsInRow[0] / 2 + config.padFrontAmountAtEachRow[0];
  const rowSplit = rows.length / 2;

  const topRows = rows.slice(0, rowSplit);
  const bottomRows = rows.slice(rowSplit);
  const leftOf = (quadrantRows: number[][]) => quadrantRows.map((row) => row.slice(0, columnSplit));
  const rightOf = (quadrantRows: number[][]) => quadrantRows.map((row) => row.slice(columnSplit));

  return [leftOf(topRows), rightOf(topRows), leftOf(bottomRows), rightOf(bottomRows)];
}

function mergeQuadrants(quadrants: number[][][]): number[][] {
  const strip = repeated(MERIDIAN_STRIP_DECIBELS, SAMPLES_BETWEEN_LOCATIONS);
  const joinSideBySide = (left: number[][], right: number[][]) =>
    left.map((row, rowIndex) => [...row, ...strip, ...right[rowIndex]]);

  const top = joinSideBySide(quadrants[0], quadrants[1]);
  const bottom = joinSideBySide(quadrants[2], quadrants[3]);
  const meridianRow = repeated(MERIDIAN_STRIP_DECIBELS, top[0].length);

  const meridianRows = Array.from({ length: SAMPLES_BETWEEN_LOCATIONS }, () => meridianRow);

  return [...top, ...meridianRows, ...bottom];
}

export function buildGrayScaleField(
  thresholds: number[],
  config: GrayScaleFieldConfig,
): GrayScaleField {
  const quadrants = splitIntoQuadrants(padRowsToRectangle(thresholds, config), config);
  const interpolated = mergeQuadrants(
    quadrants.map((quadrant) => fillVertically(fillHorizontally(quadrant))),
  );

  const columnCount = Math.max(...config.elementsInRowUpscaled);
  return config.elementsInRowUpscaled.map((visibleLength, rowIndex) => {
    const firstVisibleColumn = config.startIndexForElementsInRow[rowIndex];
    return Array.from({ length: columnCount }, (_value, column) => {
      const isVisible = column >= firstVisibleColumn && column < firstVisibleColumn + visibleLength;
      if (!isVisible) return null;
      return grayScaleTileLevel(interpolated[rowIndex][column]);
    });
  });
}
